import React from 'react'
import { buildStoreLocatorPopupObj, getLocaleStr, getTranslatedTextByKey, redirectToChainMenu } from 'utils/utils'
import { getStoreStatus } from 'utils/availabilityUtils'
import { ORDER_TYPES, STORE_STATUS } from 'utils/constants'
import { getUserLanguage } from 'utils/language'
import Card from 'static/components/StoreLocator/common/Card'
import PopupButton from './PopupButton'
import type { ListProps } from './ListProps'
import { useRouter } from 'next/router'

import { useStores } from 'hooks/useStores'

type ListRowProps = Omit<ListProps, 'updatedStores'> & {
	index: number
	stores: any[]
	rank: number
	setModalOpen: (open: boolean) => void
	setCustomError: (error: any) => void
	innerRef: React.RefObject<HTMLInputElement>
}

const ListRow = ({
	index,
	stores,
	infra,
	locale,
	rank,
	params,
	setModalOpen,
	onSelectedStore,
	setCustomError,
	hasStores,
	innerRef,
	user,
}: ListRowProps) => {
	const store: any = stores[index]
	const router = useRouter()
	const { addressManagerV2Store } = useStores()

	const updatedStore = stores.find((s) => getLocaleStr(s.title, locale).toLowerCase() === getLocaleStr(store.title, locale).toLowerCase())
	const storeStatus = getStoreStatus(store.availability)
	const startOrder = async (e: { preventDefault: () => void }) => {
		e.preventDefault()

		if (storeStatus === STORE_STATUS.UNAVAILABLE) {
			redirectToChainMenu(getUserLanguage(), router)
			return
		}

		const availableStore = updatedStore || store

		if(addressManagerV2Store.newLocalizationFlowEnabled) {
			addressManagerV2Store.start({
				id: availableStore.id,
				name: availableStore.title,
				address: availableStore.address.formatted
			})
			return
		}

		try {
			const popupChildrenObj = buildStoreLocatorPopupObj(null, availableStore, availableStore.id)

			const popupChildren = Object.keys(popupChildrenObj).map((orderType) => (
				<PopupButton
					key={orderType}
					orderType={orderType}
					btnText={
						orderType === ORDER_TYPES.DELIVERY
							? getTranslatedTextByKey(ORDER_TYPES.DELIVERY, 'Delivery')
							: getTranslatedTextByKey(ORDER_TYPES.PEAKUP, 'Pickup')
					}
					user={user}
					infra={infra}
					store={store}
					params={params}
				/>
			))
			infra.setGenericPopup({
				titleText: getTranslatedTextByKey('eCommerce.storeLocator.howWouldYouLikeToOrder', 'How would you like to order?'),
				subTitleText: getTranslatedTextByKey(
					'eCommerce.storeLocator.menuAvailabilityMayVary',
					'Menu availability and pricing may vary between order types.'
				),
				children: popupChildren,
				showClearIcon: true,
			})
			setModalOpen(true)
		} catch (error) {
			setCustomError(error)
		}
	}

	return (
		<Card
			// @ts-ignore - throws an error since the <Card /> must also be a .ts file
			data={store}
			key={store.id}
			ref={innerRef}
			locale={locale}
			rank={rank}
			onSelectStore={onSelectedStore}
			hasStores={hasStores}
			buttonProps={{
				onClick: startOrder,
				'data-testid': 'start-my-order-button',
				fullWidth: true,
			}}
			testId="store-item-card"
		/>
	)
}

export default ListRow
